import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { WIDGET_IDS } from '@/constants/widget';
import { useDashboard } from '@/contexts/DashboardContext';
import { useWidgetsData } from '@/hooks/api/useWidgetsData';
import { cn } from '@/lib/utils';
import { convertLocalTimezone } from '@/utils/dateUtils';
import { secondsToHms } from '@/utils/timeUtils';
import { Clock, Phone, UserCheck, XCircle } from "lucide-react";
import React, { useEffect, useState, useMemo } from "react";
import type { CallsDetailOverviewWidgetData } from "./CallsDetailOverviewWidget";
import { JourneyStreamDetail } from "./Timeline";

interface CallDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callDetail: CallsDetailOverviewWidgetData;
}

const CallDetailsModal: React.FC<CallDetailsModalProps> = ({
  open,
  onOpenChange,
  callDetail,
}) => {
  const [journeyEvents, setJourneyEvents] = useState<JourneyStreamDetail[]>([]);
  const [currentCallId, setCurrentCallId] = useState<string | undefined>(callDetail?.callId);
  const { theme } = useDashboard();

  useEffect(() => {
    setCurrentCallId(callDetail?.callId);
  }, [callDetail]);
  
  // Memoize the params object to prevent unnecessary re-renders
  const widgetParams = useMemo(() => ({
    callId: currentCallId,
    widgetIds: [WIDGET_IDS.CALL_JOURNEY]
  }), [currentCallId]);

  const { data, loading: callJourneyLoading } = useWidgetsData(widgetParams);

  const isOutbound = callDetail?.direction === "Outbound";

  useEffect(() => {
    if (data?.callJourney) {
      setJourneyEvents(data.callJourney);
    }
  }, [data]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        "sm:max-w-[600px] max-h-[700px] flex flex-col",
        theme === 'dark' ? "bg-[#0C3451] text-white" : "bg-white"
      )}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className={cn(
            "text-xl font-bold",
            theme === 'dark' ? "text-white" : "text-gray-900"
          )}>
            Call Details
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 gap-6 mt-4">
            <div>
              <div className="text-sm text-gray-500 dark:text-muted-foreground font-medium">Customer</div>
              <div className={cn(
                "font-semibold text-lg",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>{callDetail?.customerName}</div>

              <div className="text-sm text-gray-500 dark:text-muted-foreground font-medium mt-4">{isOutbound ? "Phone" : "Agent"}</div>
              <div className={cn(
                "font-semibold text-lg",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>{isOutbound ? callDetail?.callee : callDetail?.huntedAgent}</div>

              <div className="text-sm text-gray-500 dark:text-muted-foreground font-medium mt-4">Date & Time</div>
              <div className={cn(
                "font-semibold",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>{convertLocalTimezone(callDetail?.startTime)}</div>
            </div>

            <div>
              <div className="text-sm text-gray-500 dark:text-muted-foreground font-medium"> Organizer </div>
              <div className={cn(
                "font-semibold text-lg",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>{callDetail?.caller}</div>

              <div className="text-sm text-gray-500 dark:text-muted-foreground font-medium mt-4">Queue</div>
              <div className={cn(
                "font-semibold text-lg",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>{callDetail?.callQueue}</div>

              <div className="text-sm text-gray-500 dark:text-muted-foreground font-medium mt-4">Duration</div>
              <div className={cn(
                "font-semibold text-lg",
                theme === 'dark' ? "text-white" : "text-gray-900"
              )}>{callDetail?.callDuration}</div>

            </div>
          </div>
          <div className="mt-6 border border-gray-200 rounded-xl p-4">
            <div className="text-lg text-muted-foreground font-medium">Call Journey</div>
            {!callJourneyLoading &&
              (<div className="mt-2">
                <ol className="relative border-l-2 border-gray-200 ml-6">
                  {journeyEvents.map((event, idx) => {
                    let icon = null;
                    let iconBg = "bg-white";
                    let iconBorder = "border-blue-400";
                    let iconColor = "text-blue-600";
                    if (event.eventType === "Call initiated" || event.eventType === "Incoming call") {
                      icon = <Phone className="w-5 h-5" />;
                    } else if (event.eventType.toLowerCase().includes("queue")) {
                      icon = <Clock className="w-5 h-5" />;
                      iconColor = "text-blue-600";
                    } else if (event.eventType.toLowerCase().includes("connect")) {
                      icon = <UserCheck className="w-5 h-5" />;
                      iconBg = "bg-green-100";
                      iconColor = "text-green-600";
                      iconBorder = "border-green-400";
                    } else if (event.eventType.toLowerCase().includes("complete") || event.eventType.toLowerCase().includes("hang up")) {
                      icon = <XCircle className="w-5 h-5" />;
                      iconColor = "text-blue-600";
                    }
                    return (
                      <li className="mb-8 relative left-[-16px] pl-12" key={idx}>
                        <span
                          className={`absolute left-0 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 ${iconBg} ${iconBorder} border-2 rounded-full ${iconColor}`}
                        >
                          {icon}
                        </span>
                        <div className="flex flex-col">
                          <span className={cn(
                            "font-semibold text-gray-900 text-base",
                            theme === 'dark' ? "text-white" : "text-gray-900"
                          )}>
                            {event.eventType}
                            {event.inPeriodDuration != null &&
                            Number.isFinite(Number(event.inPeriodDuration))
                              ? ` (${secondsToHms(event.inPeriodDuration)})`
                              : ""}
                          </span>
                          <span className={cn(
                            "text-sm text-gray-500 mt-1",
                            theme === 'dark' ? "text-gray-400" : "text-gray-500"
                          )}>
                            {event.startedAt ? convertLocalTimezone(event.startedAt) : ""}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>)
            }
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CallDetailsModal; 